import { InsertDriveFile } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import GasMeterOutlinedIcon from "@mui/icons-material/GasMeterOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import InsightsIcon from "@mui/icons-material/Insights";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PeopleIcon from "@mui/icons-material/People";
import RecentActorsOutlinedIcon from "@mui/icons-material/RecentActorsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Card,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { localizationService } from "services/localization-service";
import {
  hasAnyEventRole,
  hasAnyMonitoringRole,
  roleMatch,
} from "../../actions/auth";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { UserRoles } from "../../types/enums";

const SIDEBAR_EXPANDED_KEY = "lynx-sidebar-expanded";
const SIDEBAR_GROUPS_KEY = "lynx-sidebar-groups";

export const MainSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const organization = useSelector((state) => state.organization);
  const account = useSelector((state) => state.account);
  const { width } = useWindowDimensions();
  // Hide sidebar on mobile (when mobile menu is active)
  const isMobile = width < 900;

  // Initialize sidebar state from localStorage
  const [isExpanded, setIsExpanded] = React.useState(() => {
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [expandedGroups, setExpandedGroups] = React.useState(() => {
    const saved = localStorage.getItem(SIDEBAR_GROUPS_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          operations: true,
          monitoring: true,
          analytics: true,
          lookups: true,
          administrator: false,
        };
  });

  React.useEffect(() => {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify(isExpanded));
    // Set CSS variable for dynamic sidebar width
    document.documentElement.style.setProperty(
      "--sidebar-width",
      isExpanded ? "240px" : "64px"
    );
  }, [isExpanded]);

  React.useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  const handleToggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const handleToggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const isActiveRoute = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  if (account.isLoading) {
    return <></>;
  }

  // Hide sidebar on mobile when mobile menu is active
  if (isMobile) {
    return null;
  }

  const NavItem = ({ icon: Icon, label, path, className, id, secondary }) => {
    const active = isActiveRoute(path);
    return (
      <ListItem
        disablePadding
        className={className}
        id={id}
        sx={{
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemButton
          onClick={() => handleNavigate(path)}
          selected={active}
          sx={{
            minHeight: 48,
            justifyContent: isExpanded ? "initial" : "center",
            px: 2.5,
            "&.Mui-selected": {
              backgroundColor: "rgba(243, 167, 56, 0.1)", // Subtle orange background
              "&:hover": {
                backgroundColor: "rgba(243, 167, 56, 0.15)", // Slightly more orange on hover
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isExpanded ? 3 : "auto",
              justifyContent: "center",
              color: "inherit",
            }}
          >
            {isExpanded ? (
              <Icon fontSize="small" />
            ) : (
              <Tooltip title={label} placement="right" arrow>
                <Icon fontSize="small" />
              </Tooltip>
            )}
          </ListItemIcon>
          {isExpanded && (
            <ListItemText
              primary={label}
              secondary={secondary}
              secondaryTypographyProps={{
                sx: {
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  lineHeight: 1.2,
                },
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const NavGroup = ({ title, children, groupKey }) => {
    if (!isExpanded) {
      return <>{children}</>;
    }

    const isOpen = expandedGroups[groupKey] ?? true;

    return (
      <>
        <ListItem
          disablePadding
          onClick={() => handleToggleGroup(groupKey)}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ListItemButton
            sx={{
              minHeight: 40,
              px: 2.5,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: 2, flexShrink: 0 }}>
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemIcon>
            <ListItemText
              sx={{
                overflow: "hidden",
                "& .MuiListItemText-primary": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              primary={
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {title}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </>
    );
  };

  return (
    <Box
      component="aside"
      id="mainSidebar"
      className="sidebar"
      sx={{
        position: "fixed",
        marginTop: "50px",
        top: 0,
        left: 0,
        bottom: 0,
        width: isExpanded ? 240 : 64,
        backgroundColor: "background.paper",
        zIndex: 99,
        transition: "width 0.3s ease",
        display: "flex",
        flexDirection: "column",
        boxShadow: 2,
        overflow: "hidden",
      }}
    >
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          boxShadow: "none",
        }}
      >
        {/* Toggle Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isExpanded ? "flex-end" : "center",
            padding: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <IconButton
            onClick={handleToggleSidebar}
            size="small"
            sx={{ color: "text.secondary" }}
            aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>

        {/* Navigation List */}
        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            py: 0,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.3)",
              },
            },
          }}
        >
          {/* Core Navigation - Always visible */}
          <NavItem
            icon={MonitorOutlinedIcon}
            label={localizationService.getLocalizedString(
              "dashboard.dashboard"
            )}
            path="/dashboard"
            className="sidebar-dashboard"
          />
          <NavItem
            icon={MapOutlinedIcon}
            label={localizationService.getLocalizedString("map.map")}
            path="/map"
            className="sidebar-map"
          />

          {/* Operations Group */}
          {(hasAnyEventRole() ||
            roleMatch([
              UserRoles.InspectionsUser,
              UserRoles.IncidentsUser,
            ])) && (
            <>
              <Divider sx={{ my: 1 }} />
              <NavGroup title="Operations" groupKey="operations">
                {hasAnyEventRole() && (
                  <NavItem
                    icon={EventOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "events.events"
                    )}
                    path="/events"
                    className="sidebar-events"
                  />
                )}
                {hasAnyEventRole() && organization.hasCommitments && (
                  <NavItem
                    icon={HandshakeOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "commitments.commitments"
                    )}
                    path="/commitments"
                    className="sidebar-volunteers"
                  />
                )}
                {roleMatch([UserRoles.InspectionsUser]) && (
                  <NavItem
                    icon={ContentPasteIcon}
                    label={localizationService.getLocalizedString(
                      "inspections.inspections"
                    )}
                    path="/inspections"
                    className="sidebar-inspections"
                  />
                )}
                {roleMatch([UserRoles.IncidentsUser]) && (
                  <NavItem
                    icon={WarningAmberIcon}
                    label={localizationService.getLocalizedString(
                      "incidents.incidents"
                    )}
                    path="/incidents"
                    className="sidebar-incidents"
                  />
                )}
                {(hasAnyEventRole() ||
                  roleMatch([UserRoles.IncidentsUser])) && (
                  <NavItem
                    icon={PendingActionsIcon}
                    label={localizationService.getLocalizedString(
                      "actions.actions"
                    )}
                    path="/actions"
                    className="sidebar-actions"
                  />
                )}
                {hasAnyEventRole() && organization.hasTraining && (
                  <NavItem
                    icon={SchoolOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "training.training"
                    )}
                    path="/training"
                    className="sidebar-training"
                  />
                )}
              </NavGroup>
            </>
          )}

          {/* Monitoring & Compliance Group */}
          {(hasAnyMonitoringRole() || organization.hasPermitFeature) && (
            <>
              <Divider sx={{ my: 1 }} />
              <NavGroup title="Monitoring & Compliance" groupKey="monitoring">
                {hasAnyMonitoringRole() && (
                  <NavItem
                    icon={GasMeterOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "monitoring.monitoring"
                    )}
                    path="/monitoring"
                    className="sidebar-monitoring"
                    id="monitoringSidebarRow"
                  />
                )}
                {organization.hasPermitFeature && (
                  <NavItem
                    icon={GavelOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "permits.permits"
                    )}
                    path="/permits"
                    className="sidebar-permits"
                    secondary="Permits, Agreements, and Licenses"
                  />
                )}
              </NavGroup>
            </>
          )}

          {/* Analytics & Reporting Group */}
          <Divider sx={{ my: 1 }} />
          <NavGroup title="Analytics & Reporting" groupKey="analytics">
            <NavItem
              icon={InsightsIcon}
              label={localizationService.getLocalizedString("kpis.kpis")}
              path="/kpis"
            />
            <NavItem
              icon={InsertDriveFile}
              label={localizationService.getLocalizedString(
                "documents.documents"
              )}
              path="/documents"
              className="sidebar-documents"
            />
          </NavGroup>

          {/* Lookups & Reference Data Group */}
          {(hasAnyEventRole() || roleMatch([UserRoles.InspectionsUser])) && (
            <>
              <Divider sx={{ my: 1 }} />
              <NavGroup title="Lookups & Reference Data" groupKey="lookups">
                {hasAnyEventRole() && (
                  <NavItem
                    icon={RecentActorsOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "contacts.contacts"
                    )}
                    path="/contacts"
                  />
                )}
                {roleMatch([UserRoles.InspectionsUser]) && (
                  <NavItem
                    icon={ConstructionOutlinedIcon}
                    label={localizationService.getLocalizedString(
                      "equipment.equipment"
                    )}
                    path="/equipment"
                    className="sidebar-equipment"
                  />
                )}
              </NavGroup>
            </>
          )}

          {/* Administrator - Always at bottom */}
          <Box sx={{ mt: "auto" }}>
            <Divider sx={{ my: 1 }} />
            <NavGroup title="Administrator" groupKey="administrator">
              {roleMatch([UserRoles.Admin]) && (
                <NavItem
                  icon={FactoryOutlinedIcon}
                  label={localizationService.getLocalizedString(
                    "assets.assets"
                  )}
                  path="/assets"
                  className="sidebar-assets"
                />
              )}
              {organization.featureFlags.showInDevelopmentFeatures && (
                <NavItem
                  icon={PeopleIcon}
                  label={
                    localizationService.getLocalizedString(
                      "screen.sidebar.communityPortal1"
                    ) +
                    " " +
                    localizationService.getLocalizedString(
                      "screen.sidebar.communityPortal2"
                    )
                  }
                  path="/portal-submissions"
                />
              )}
              <NavItem
                icon={SettingsOutlinedIcon}
                label={localizationService.getLocalizedString(
                  "settings.settings"
                )}
                path="/settings"
                className="sidebar-settings"
              />
            </NavGroup>
          </Box>
        </List>
      </Card>
    </Box>
  );
};
